import { UkDateInput } from "@/components/UkDateInput";
import { GpCard } from "@/components/family/GpCard";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LogOut, ArrowLeft, Eye, CalendarDays, Clock, User, Sparkles, MapPin, Mail, HandHeart, Plus, Check, Phone, Star } from "lucide-react";
import { statusTone } from "@/lib/status";
import { SERVICES } from "@/lib/services";
import { toast } from "sonner";
import { galleryUrl } from "@/components/ActivityGallery";
import { AboutClient, AppointmentForm, ExtraDaysForm, MyFamilyRequests, postFamilyRequest } from "@/components/family/FamilyForms";
import { FamilyAgreement } from "@/components/agreements/FamilyAgreement";
import { BundleCounter } from "@/components/Bundles";

export default function FamilyPortal({ preview = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [services, setServices] = useState([]);
  const [picked, setPicked] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hol, setHol] = useState({ start_date: "", end_date: "", reason: "" });
  const [famReqs, setFamReqs] = useState([]);
  const loadFamReqs = () => api.get("/family-requests").then((r) => setFamReqs(r.data)).catch(() => {});
  const token = localStorage.getItem("bsb_token");

  useEffect(() => {
    const url = preview ? `/family/overview/${clientId}` : "/family/overview";
    api.get(url).then((r) => setData(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => setServices(SERVICES));
    if (!preview) {
      api.get("/messages").then((r) => setMsgs(r.data)).catch(() => {});
      api.get("/service-requests").then((r) => setReqs(r.data)).catch(() => {});
      loadFamReqs();
    }
  }, [preview, clientId]);

  const handleLogout = () => { logout(); navigate("/login"); };
  const markRead = (mid) => api.post(`/messages/${mid}/read`).then(() => setMsgs((l) => l.map((m) => (m.id === mid ? { ...m, read: true } : m)))).catch(() => {});

  const submitRequest = async () => {
    if (!picked) { toast.error("Choose a service to request"); return; }
    setSubmitting(true);
    try {
      await api.post("/service-requests", { service: picked, notes: note });
      toast.success("Request sent — the team will be in touch");
      setPicked(""); setNote("");
      const r = await api.get("/service-requests"); setReqs(r.data);
    } catch { toast.error("Could not send your request. Please try again."); }
    finally { setSubmitting(false); }
  };

  const submitHoliday = async () => {
    if (!hol.start_date || !hol.end_date) { toast.error("Please choose both dates"); return; }
    try {
      await api.post("/leave", { subject_type: "client", subject_id: data.client.id, ...hol });
      toast.success("Holiday request sent to the office");
      setHol({ start_date: "", end_date: "", reason: "" });
    } catch (e) { toast.error("Could not send your holiday request. Please try again."); }
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="family-portal">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-brand/10 bg-white/95 px-5 py-4 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Brightside" className="h-9 w-9 rounded-xl object-cover" />
          <div className="leading-tight">
            <p className="font-heading text-sm font-bold text-ink">Brightside · Family</p>
            <p className="text-[11px] text-ink-muted">Befriending Services</p>
          </div>
        </div>
        {preview ? (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gold-light px-3 py-1 text-xs font-semibold text-gold-dark"><Eye size={13} /> Preview</span>
            <button onClick={() => navigate(-1)} data-testid="family-preview-back" className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-ink hover:bg-secondary"><ArrowLeft size={16} /> Back</button>
          </div>
        ) : (
          <button onClick={handleLogout} data-testid="family-logout-button" className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-ink hover:bg-secondary">
            <LogOut size={16} /> Sign out
          </button>
        )}
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        {!data ? (
          <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" /></div>
        ) : (
          <div className="space-y-8">
            <div>
              <p className="text-sm font-medium uppercase tracking-wider text-ink-muted">{preview ? "Family portal preview" : `Welcome, ${user?.name?.split(" ")[0]}`}</p>
              <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-ink">
                Care updates for {data.client?.name || "your family member"}
              </h1>
              {data.client?.care_needs && <p className="mt-2 text-ink-soft">{data.client.care_needs}</p>}
              {data.client?.address && <p className="mt-1 flex items-center gap-1.5 text-sm text-ink-muted"><MapPin size={14} />{data.client.address}</p>}
            </div>

            {data.client && <AboutClient client={data.client} onCorrection={(notes) => (preview ? Promise.resolve(false) : postFamilyRequest({ kind: "correction", notes }, loadFamReqs))} />}
            {data.client && <GpCard key={data.client.id} gp={data.client.gp} canEdit={!preview} />}
            <BundleCounter bundles={data.bundles} />

            {data.photo_of_week && (
              <section data-testid="photo-of-week" className="overflow-hidden rounded-2xl border border-gold/30 bg-white shadow-sm sm:flex">
                <img src={galleryUrl(data.photo_of_week.staff_id, data.photo_of_week.photo_id)} alt={data.photo_of_week.caption || "Photo of the week"}
                  data-testid="photo-of-week-image" className="h-56 w-full object-cover sm:h-auto sm:w-1/2" />
                <div className="flex flex-col justify-center gap-2 p-6">
                  <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-gold-light px-3 py-1 text-xs font-bold uppercase tracking-wider text-gold-dark"><Star size={13} /> Photo of the week</span>
                  {data.photo_of_week.caption && <p className="font-heading text-xl font-semibold text-ink" data-testid="photo-of-week-caption">{data.photo_of_week.caption}</p>}
                  <p className="text-sm text-ink-soft">Shared by {data.photo_of_week.befriender_name}, your befriender.</p>
                </div>
              </section>
            )}

            {!preview && msgs.length > 0 && (
              <section data-testid="family-messages">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-semibold text-ink"><Mail size={20} className="text-brand" /> Messages</h2>
                <div className="space-y-3">
                  {msgs.map((m) => (
                    <div key={m.id} data-testid={`family-msg-${m.id}`} className={`rounded-xl border p-4 ${m.read ? "border-border bg-white" : "border-brand/30 bg-brand-light/40"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-ink">{m.subject}</p>
                        {m.priority !== "normal" && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${m.priority === "urgent" ? "bg-coral-light text-coral-dark" : "bg-gold-light text-gold-dark"}`}>{m.priority}</span>}
                      </div>
                      {m.body && <p className="mt-1 whitespace-pre-wrap text-sm text-ink-soft">{m.body}</p>}
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-xs text-ink-muted">From {m.sender_name}</span>
                        {!m.read && <button onClick={() => markRead(m.id)} data-testid={`family-msg-read-${m.id}`} className="text-xs font-semibold text-brand hover:underline">Mark as read</button>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {!preview && (
              <section data-testid="request-service-section">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-semibold text-ink"><HandHeart size={20} className="text-brand" /> Request a service</h2>
                <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
                  <p className="mb-3 text-sm text-ink-soft">Pick a service you'd like for {data.client?.name?.split(" ")[0] || "your family member"} and the team will be in touch to arrange it.</p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {services.map((s) => (
                      <button key={s.name} type="button" data-testid={`service-option-${s.name}`} onClick={() => setPicked(s.name)}
                        className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-all ${picked === s.name ? "border-brand bg-brand-light/50 ring-1 ring-brand" : "border-border hover:border-brand/40 hover:bg-secondary"}`}>
                        <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${picked === s.name ? "border-brand bg-brand text-white" : "border-ink-muted"}`}>{picked === s.name && <Check size={11} />}</span>
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-baseline gap-x-2">
                            <span className="text-sm font-semibold text-ink">{s.name}</span>
                            {s.price_from != null && <span className="text-xs font-bold text-brand">from £{s.price_from} <span className="font-medium text-ink-muted">{s.unit}</span></span>}
                          </span>
                          <span className="block text-xs text-ink-muted">{s.desc}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-ink-muted">Prices from bsbservices.co.uk. Every arrangement is invoiced in advance, and the office will confirm the exact price with you before anything starts.</p>
                  <textarea data-testid="request-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Anything you'd like us to know? (optional)"
                    className="mt-3 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
                  <button onClick={submitRequest} disabled={submitting || !picked} data-testid="submit-service-request-button"
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60">
                    <Plus size={17} /> {submitting ? "Sending…" : "Send request"}
                  </button>

                  {reqs.length > 0 && (
                    <div className="mt-5 border-t border-border pt-4">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Your requests</p>
                      <div className="space-y-2">
                        {reqs.map((r) => (
                          <div key={r.id} data-testid={`family-request-${r.id}`} className="flex items-center justify-between gap-2 rounded-lg bg-ivory px-3 py-2">
                            <span className="text-sm text-ink">{r.service}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${r.status === "pending" ? "bg-gold-light text-gold-dark" : r.status === "approved" ? "bg-sage-light text-sage-dark" : "bg-coral-light text-coral-dark"}`}>{r.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            {!preview && <FamilyAgreement />}
            {!preview && <AppointmentForm onSubmit={(b) => postFamilyRequest(b, loadFamReqs)} />}
            {!preview && <ExtraDaysForm services={(data.client?.befriending_services?.length ? data.client.befriending_services : services.map((s) => s.name))} onSubmit={(b) => postFamilyRequest(b, loadFamReqs)} />}
            {!preview && <MyFamilyRequests rows={famReqs} />}

            <section data-testid="office-contact">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-brand/20 bg-brand-light/40 p-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-brand">Need to talk to us?</span>
                <a href="tel:+447761329702" className="inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-brand"><Phone size={15} /> 07761 329702</a>
                <a href="mailto:hello@bsbservices.co.uk" className="inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-brand"><Mail size={15} /> hello@bsbservices.co.uk</a>
              </div>
            </section>

            {!preview && (
              <section data-testid="holiday-request-section">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-semibold text-ink"><CalendarDays size={20} className="text-brand" /> Request a holiday</h2>
                <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
                  <p className="mb-3 text-sm text-ink-soft">Let us know dates {data.client?.name?.split(" ")[0] || "your family member"} will be away, so we can pause visits.</p>
                  <div className="flex flex-wrap gap-3">
                    <div><label className="mb-1 block text-xs font-medium text-ink-muted">From</label><UkDateInput testid="holiday-start" value={hol.start_date} onChange={(v) => setHol({ ...hol, start_date: v })} /></div>
                    <div><label className="mb-1 block text-xs font-medium text-ink-muted">To</label><UkDateInput testid="holiday-end" value={hol.end_date} min={hol.start_date} onChange={(v) => setHol({ ...hol, end_date: v })} /></div>
                    <div className="flex-1 min-w-[160px]"><label className="mb-1 block text-xs font-medium text-ink-muted">Note (optional)</label><input type="text" value={hol.reason} onChange={(e) => setHol({ ...hol, reason: e.target.value })} placeholder="e.g. visiting family" className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand" /></div>
                  </div>
                  <button onClick={submitHoliday} data-testid="submit-holiday-button" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark">Send holiday request</button>
                </div>
              </section>
            )}

            {data.befrienders?.length > 0 && (
              <section data-testid="family-befrienders">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-semibold text-ink"><HandHeart size={20} className="text-brand" /> {data.befrienders.length > 1 ? "Your befrienders" : "Your befriender"}</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {data.befrienders.map((b, i) => (
                    <div key={b.id || i} data-testid={`family-befriender-${i}`} className="rounded-xl border border-border bg-white p-5 shadow-sm">
                      <div className="flex items-center gap-4">
                        {b.photo
                          ? <img src={b.photo} alt={b.name} className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-brand/20" />
                          : <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-brand-light text-xl font-bold text-brand">{(b.name || "?").charAt(0)}</span>}
                        <div className="min-w-0">
                          <p className="font-heading text-lg font-semibold text-ink">{b.name}</p>
                          <p className="text-sm text-ink-muted">Your befriender</p>
                        </div>
                      </div>
                      {b.match_note && <p className="mt-3 rounded-lg bg-sage-light/60 px-3 py-2 text-sm text-sage-dark"><span className="font-semibold">Why they're a great match:</span> {b.match_note}</p>}
                      {b.bio && <p className="mt-3 text-sm leading-relaxed text-ink-soft">{b.bio}</p>}
                      {b.interests && <p className="mt-3 text-sm text-ink"><span className="font-semibold text-brand">Enjoys:</span> {b.interests}</p>}
                      {b.activities && <p className="mt-1 text-sm text-ink"><span className="font-semibold text-brand">Likes to do:</span> {b.activities}</p>}
                      {b.gallery?.length > 0 && (
                        <div className="mt-4 grid grid-cols-3 gap-2" data-testid={`family-befriender-gallery-${i}`}>
                          {b.gallery.map((g) => (
                            <figure key={g.id} className="overflow-hidden rounded-lg border border-border">
                              <img src={galleryUrl(b.id, g.id)} alt={g.caption || `${b.name} activity`} className="h-24 w-full object-cover" data-testid={`family-gallery-photo-${g.id}`} />
                              {g.caption && <figcaption className="px-2 py-1 text-xs text-ink-soft">{g.caption}</figcaption>}
                            </figure>
                          ))}
                        </div>
                      )}
                      {!b.bio && !b.interests && !b.activities && !b.match_note && !b.gallery?.length && <p className="mt-3 text-sm text-ink-muted">Profile coming soon.</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-semibold text-ink"><CalendarDays size={20} className="text-brand" /> Upcoming visits</h2>
              {data.upcoming.length === 0 ? (
                <p className="rounded-xl border border-border bg-white p-6 text-center text-ink-muted">No upcoming visits scheduled.</p>
              ) : (
                <div className="space-y-3">
                  {data.upcoming.map((v) => (
                    <div key={v.id} data-testid={`family-upcoming-${v.id}`} className="flex items-center gap-4 rounded-xl border border-border bg-white p-4 shadow-sm">
                      <div className="flex flex-col items-center rounded-lg bg-brand-light px-3 py-2">
                        <span className="text-[11px] font-medium text-brand">{new Date(v.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                        <span className="flex items-center gap-1 text-xs font-semibold text-ink"><Clock size={11} />{v.start_time}</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-ink">{v.start_time}–{v.end_time}</p>
                        <p className="flex items-center gap-1 text-sm text-ink-soft"><User size={13} />{v.carer_name}</p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusTone(v.status)}`}>{v.status.replace("_", " ")}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-semibold text-ink"><Sparkles size={20} className="text-gold-dark" /> Recent visit summaries</h2>
              {data.past.length === 0 ? (
                <p className="rounded-xl border border-border bg-white p-6 text-center text-ink-muted">No completed visits yet.</p>
              ) : (
                <div className="space-y-3">
                  {data.past.map((v) => (
                    <div key={v.id} data-testid={`family-past-${v.id}`} className="rounded-xl border border-border bg-white p-5 shadow-sm">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-ink">{new Date(v.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long" })}</p>
                        <span className="text-sm text-ink-muted">with {v.carer_name}</span>
                      </div>
                      {v.ai_summary ? (
                        <p className="mt-3 whitespace-pre-wrap rounded-lg bg-ivory p-4 text-sm leading-relaxed text-ink">{v.ai_summary}</p>
                      ) : v.notes ? (
                        <p className="mt-3 text-sm text-ink-soft">{v.notes}</p>
                      ) : (
                        <p className="mt-3 text-sm text-ink-muted">Visit completed. No written summary added.</p>
                      )}
                      {v.photos?.length > 0 && (
                        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                          {v.photos.map((p) => (
                            <img key={p.id} data-testid={`family-photo-${p.id}`}
                              src={`${process.env.REACT_APP_BACKEND_URL}/api/visits/${v.id}/photos/${p.id}/download?auth=${token}`}
                              alt="visit" className="h-24 w-full rounded-lg border border-border object-cover" />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
